import { uid } from "@/utils/functions";
import { Button } from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { ChangeEvent, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { CiImageOn } from "react-icons/ci";
import { FaRegFilePdf } from "react-icons/fa";
import { IoClose } from "react-icons/io5";
import { PiPaperclipHorizontal } from "react-icons/pi";

export type FileUploadProps = {
  url: string | null;
  fileName: string | null;
  progress?: number;
  compressionProgress?: number;
  error?: string | null;
};

// UploadSpot is a wrapper component for UploadSpots that handles a single file upload
export function UploadSpot({
  value,
  onChange,
  isButton,
  fileType,
}: {
  value?: FileUploadProps | string; // Can be a FileUploadProps object or a simple URL string
  onChange: (newValue: FileUploadProps) => void; // Callback when the upload changes
  isButton?: boolean; // Optional flag for rendering as a button style
  fileType?: string; // Type of file (e.g., "image/*", "application/pdf")
}) {
  // Convert string value (URL) to FileUploadProps format if needed
  const fileUploadProps =
    typeof value === "string" && value !== ""
      ? {
          fileName: null,
          url: value,
          progress: 100,
          compressionProgress: 0,
          error: null,
        }
      : value;

  return (
    <UploadSpots
      fileType={fileType}
      isButton={isButton}
      value={fileUploadProps ? [fileUploadProps] : []}
      onChange={(newValues) => {
        onChange(newValues[0]); // Only care about the first file
      }}
    />
  );
}

// UploadSpots handles multiple file uploads with progress tracking, previews, and deletion
export function UploadSpots({
  value,
  onChange,
  isButton,
  fileType,
}: {
  value: FileUploadProps[]; // Array of uploaded files
  onChange: (newValue: FileUploadProps[]) => void; // Callback when the upload list changes
  isButton?: boolean; // Toggle between preview vs. button style
  fileType?: string; // Type of file allowed
}) {
  const inputRef = useRef(null); // Ref to the hidden file input element
  const { t } = useTranslation();

  // Convert input value array to an object keyed by fileName
  let initialValue: Record<string, FileUploadProps> = {};
  for (let item of value || []) {
    item.fileName ||= uid(); // Generate UID if not provided
    initialValue[item.fileName] = item;
  }

  const [overriddenFile, setOverriddenFile] = useState<string | null>(""); // Tracks file being replaced
  const [files, setFiles] = useState<Record<string, File>>({}); // Raw file objects
  const [uploads, setUploads] =
    useState<Record<string, FileUploadProps>>(initialValue); // Upload status per file

  // Handles file selection
  async function handleChange(e: ChangeEvent<HTMLInputElement>) {
    if (!e.target.files) return;
    let newFiles = Array.from(e.target.files);

    // If a file is being overridden, remove the old one
    if (overriddenFile) {
      delete uploads[overriddenFile];
      setOverriddenFile(null);
    }

    // Add each file to uploads and start uploading
    for (let file of newFiles) {
      uploads[file.name] = {
        fileName: file.name,
        error: null,
        url: null,
        progress: 0,
        compressionProgress: 0,
      };
      handleSubmit(file);
    }

    setUploads(uploads); // Update state
  }

  // Update a single file's upload state
  function addToUploadProp(
    fileName: string,
    newProp: Partial<FileUploadProps>
  ) {
    uploads[fileName] = {
      ...uploads[fileName],
      ...newProp,
    };
    setUploads({ ...uploads });
  }

  // Handles actual file upload logic using axios
  async function handleSubmit(file: File | null | undefined) {
    const url =
      fileType == "application/pdf" ? `/api/uploads/pdfs` : `/api/uploads/`; // Different endpoint for PDFs
    const formData = new FormData();
    if (!file) return;

    files[file.name] = file;
    setFiles({ ...files });

    formData.append("file", file);
    formData.append("fileName", file.name);

    axios
      .post(url, formData, {
        maxContentLength: 100_000_000,
        maxBodyLength: 100_000_000,
        headers: {
          "content-type": "multipart/form-data",
        },
        onUploadProgress: function (progressEvent: any) {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );

          addToUploadProp(file.name, { progress: percentCompleted }); // Update progress
        },
      })
      .then(async (res) => {
        addToUploadProp(file.name, { url: res.data.url }); // Set uploaded file URL
        onChange?.([uploads[file.name]]); // Notify parent
      })
      .catch((err) => {
        addToUploadProp(file.name, {
          error: JSON.stringify(err), // Save error
        });
      });
  }

  return (
    <div className="image_input form-group">
      {/* Hidden file input for triggering uploads */}
      <input
        ref={inputRef}
        type="file"
        onChange={handleChange}
        accept={fileType ? fileType : "image/*"}
        style={{ display: "none" }}
      />

      <div className="grid gap15">
        {value.length != 0 &&
          Object.values(uploads)?.map((uploadInfo) => {
            const file = files[uploadInfo.fileName || ""];
            let uploadedUrl = uploadInfo?.url;

            // If file not uploaded yet, generate temporary preview
            if (!uploadedUrl && file) uploadedUrl = URL.createObjectURL(file);

            const { progress, error } = uploadInfo;

            return (
              <div key={uploadInfo.url} className="relative">
                {/* File preview UI */}
                {!isButton &&
                  progress == 100 &&
                  uploadedUrl != null &&
                  uploadedUrl &&
                  !uploadedUrl.includes("blob") &&
                  !uploadedUrl.includes("http") && (
                    <div
                      className="image-preview"
                      key={uid()}
                      onClick={(e) => {
                        e.stopPropagation();
                        setOverriddenFile(uploadInfo.fileName || "");
                        let current: any = inputRef.current;
                        current?.click?.();
                      }}
                    >
                      {/* Determine preview type */}
                      {uploadedUrl != " " && (
                        <>
                          {fileType?.includes("video") ||
                          file?.type?.startsWith("video/") ? (
                            <video
                              src={`/api/images/uploads/${uploadedUrl}`}
                              controls
                              width="100%"
                              style={{ borderRadius: "8px" }}
                            />
                          ) : fileType?.includes("pdf") ? (
                            <div className="c1 center">
                              <FaRegFilePdf size={48} />
                              <p className="f15">
                                {uploadedUrl.split("/").pop()}
                              </p>
                            </div>
                          ) : (
                            <img
                              src={`/api/images/uploads/${uploadedUrl}`}
                              alt="Uploaded content"
                            />
                          )}

                          {/* Delete button */}
                          <button
                            className="action right pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              delete uploads[uploadInfo.fileName || ""];
                              onChange?.(
                                Object.values(uploads).filter(
                                  (upload) =>
                                    upload.url != "" && upload.url != null
                                )
                              );
                            }}
                          >
                            <IoClose size={15} color="black" />
                          </button>
                        </>
                      )}
                      {uploadedUrl == " " && (
                        <PiPaperclipHorizontal color="black" />
                      )}
                    </div>
                  )}

                {/* Upload progress loader */}
                {!isButton &&
                  (uploadedUrl?.startsWith("blob") ||
                    uploadedUrl?.startsWith("http")) && (
                    <div className="image-preview">
                      <CircularProgress value={progress} />
                    </div>
                  )}

                {/* Button-style uploader */}
                {isButton && (
                  <div
                    className="input-container"
                    key={uid()}
                    onClick={(e) => {
                      e.stopPropagation();
                      setOverriddenFile(uploadInfo.fileName || "");
                      let current: any = inputRef.current;
                      current?.click?.();
                    }}
                  >
                    {uploadedUrl?.startsWith("blob") ||
                    uploadedUrl?.startsWith("http") ? (
                      <label
                        className={
                          !fileType?.includes("pdf")
                            ? "image-input-button"
                            : "br-blue pointer br5 grid center p10 "
                        }
                      >
                        <CircularProgress size={15} />
                      </label>
                    ) : (
                      <label
                        className={
                          !fileType?.includes("pdf")
                            ? "image-input-button"
                            : " br-blue pointer br5 grid c1-auto gap10 vcenter p10 "
                        }
                      >
                        {fileType?.includes("pdf") ? (
                          <>
                            <p>{t("addDocument")}</p> <FaRegFilePdf />
                          </>
                        ) : (
                          <CiImageOn />
                        )}
                      </label>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>

      {/* Default file upload box when no file is uploaded */}
      {value.length === 0 && !isButton && (
        <div
          key={uid()}
          className="image-preview"
          onClick={(e) => {
            e.stopPropagation();
            setOverriddenFile(null);
            let current: any = inputRef.current;
            current?.click?.();
          }}
        >
          <PiPaperclipHorizontal color="black" />
        </div>
      )}
    </div>
  );
}
